import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const API = "https://erp-xe9u.onrender.com/api";

const ACCENT = "#f97316";

const PDFJS_VERSION = "3.11.174";
const PDFJS_SRC = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

// Plain fetch on purpose: no auth headers, no axios interceptors.
async function api(path, opts = {}) {
  const res = await fetch(`${API}${path}`, opts);
  if (!res.ok) {
    let message = "Something went wrong.";
    try {
      message = (await res.json()).message || message;
    } catch {
      /* non-JSON error */
    }
    throw new Error(message);
  }
  return res;
}

// Loads pdf.js from the CDN once (no install / bundler config needed).
let pdfjsPromise = null;
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!pdfjsPromise) {
    pdfjsPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = PDFJS_SRC;
      s.async = true;
      s.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        resolve(window.pdfjsLib);
      };
      s.onerror = () => {
        pdfjsPromise = null;
        reject(new Error("Could not load the PDF viewer."));
      };
      document.head.appendChild(s);
    });
  }
  return pdfjsPromise;
}

// ── Signature pad popup ─────────────────────────────────────────────────────
function SignaturePadModal({ onCancel, onApply }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const up = () => {
    drawing.current = false;
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-3">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h2 className="text-sm font-semibold text-white">
            Draw your signature
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-300 text-lg leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-4 space-y-3">
          <canvas
            ref={canvasRef}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            onPointerLeave={up}
            className="w-full bg-white rounded border border-gray-600"
            style={{ aspectRatio: "19 / 6", touchAction: "none" }}
          />
          <p className="text-[11px] text-gray-500">
            Sign inside the box. It will be placed on the signature line.
          </p>
        </div>
        <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
          <button
            onClick={clear}
            className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1"
          >
            Clear
          </button>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              disabled={!hasInk}
              onClick={() => onApply(canvasRef.current.toDataURL("image/png"))}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 disabled:opacity-40"
              style={{ backgroundColor: ACCENT }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Renders EVERY page of the PDF as a stacked canvas, scaled to the screen width.
// If sigField is given, a clickable box is placed over the signature line.
function PdfPages({ buffer, zoom, sigField, sigImage, onSign }) {
  const wrapRef = useRef(null);
  const pagesRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [pageCount, setPageCount] = useState(0);
  const [sigHost, setSigHost] = useState(null); // { el, scale }

  // Track container width (only width, so mobile URL-bar height changes don't re-render)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      setWidth((old) => (Math.abs(old - w) > 2 ? w : old));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  useEffect(() => {
    if (!buffer || !width) return;
    let cancelled = false;
    let doc = null;

    (async () => {
      try {
        setStatus("loading");
        setSigHost(null);
        const pdfjsLib = await loadPdfJs();
        // slice(0): pdf.js transfers the buffer to its worker, so give it a copy
        doc = await pdfjsLib.getDocument({ data: buffer.slice(0) }).promise;
        if (cancelled) return;
        setPageCount(doc.numPages);

        const container = pagesRef.current;
        container.innerHTML = "";
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const targetWidth = width * zoom;

        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const cssScale = targetWidth / base.width;
          const viewport = page.getViewport({ scale: cssScale * dpr });

          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          const cssW = `${Math.floor(viewport.width / dpr)}px`;
          const cssH = `${Math.floor(viewport.height / dpr)}px`;
          canvas.style.width = cssW;
          canvas.style.height = cssH;
          canvas.style.display = "block";
          canvas.style.background = "#fff";
          canvas.style.maxWidth = "none";

          // Per-page wrapper so an overlay can be positioned on the page
          const wrap = document.createElement("div");
          wrap.style.position = "relative";
          wrap.style.width = cssW;
          wrap.style.height = cssH;
          wrap.style.margin = "0 auto 8px";
          wrap.appendChild(canvas);
          container.appendChild(wrap);

          if (sigField && n - 1 === sigField.page)
            setSigHost({ el: wrap, scale: cssScale });

          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          }).promise;
          if (n === 1) setStatus("ready"); // show page 1 as soon as it's drawn
        }
        if (!cancelled) setStatus("ready");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      if (doc) doc.destroy();
    };
  }, [buffer, width, zoom, sigField]);

  return (
    <div ref={wrapRef} className="w-full">
      {status === "loading" && (
        <p className="p-6 text-sm text-gray-500 animate-pulse">
          Loading document…
        </p>
      )}
      {status === "error" && (
        <p className="p-6 text-sm text-red-400">
          Couldn't display the document here. Use "Open PDF in a new tab" below.
        </p>
      )}
      <div
        className="overflow-auto bg-gray-700 p-2 rounded-b"
        style={{
          maxHeight: "75vh",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y pinch-zoom",
          display: status === "error" ? "none" : "block",
        }}
      >
        <div ref={pagesRef} />
      </div>
      {status === "ready" && pageCount > 0 && (
        <p className="px-4 py-1 text-[11px] text-gray-500">
          {pageCount} page{pageCount === 1 ? "" : "s"} · scroll to read all
        </p>
      )}

      {sigHost &&
        sigField &&
        createPortal(
          <button
            id="sig-field"
            type="button"
            onClick={onSign}
            aria-label="Sign here"
            className={
              "absolute flex items-center justify-center rounded border-2 border-dashed " +
              (sigImage
                ? "border-green-500 bg-white/60"
                : "border-orange-500 bg-orange-500/15 animate-pulse")
            }
            style={{
              left: sigField.x * sigHost.scale,
              top: sigField.y * sigHost.scale,
              width: sigField.w * sigHost.scale,
              height: sigField.h * sigHost.scale,
              minHeight: 28,
              padding: 0,
              cursor: "pointer",
            }}
          >
            {sigImage ? (
              <img
                src={sigImage}
                alt="Your signature"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <span
                className="text-orange-600 font-semibold"
                style={{ fontSize: Math.max(10, 11 * sigHost.scale) }}
              >
                ✍ Click to sign
              </span>
            )}
          </button>,
          sigHost.el,
        )}
    </div>
  );
}

export default function SignAgreement() {
  // Path is /sign/<token>
  const token = window.location.pathname.split("/").filter(Boolean)[1] || "";

  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [zoom, setZoom] = useState(1);

  const [agree, setAgree] = useState(false);
  const [sigImage, setSigImage] = useState("");
  const [padOpen, setPadOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const loadPdf = async () => {
    const res = await api(`/sign/${token}/pdf`);
    const blob = await res.blob();
    const buf = await blob.arrayBuffer();
    setPdfBuffer(buf);
    setPdfUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(blob);
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api(`/sign/${token}`);
        const data = await res.json();
        setInfo(data);
        if (data.signed) setDone(true);
        await loadPdf();
      } catch (e) {
        setErr(e.message || "This signing link is invalid or has expired.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    setErr("");
    if (!sigImage)
      return setErr(
        info.signatureField
          ? "Please click the signature line in the document to sign."
          : "Please add your signature.",
      );
    if (!agree) return setErr("Please confirm you agree to sign.");
    setBusy(true);
    try {
      await api(`/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature: sigImage }),
      });
      setInfo((i) => ({
        ...i,
        signedBy: i.subName || "",
        signatureField: null,
      }));
      setDone(true);
      setZoom(1);
      await loadPdf(); // show the signed copy
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const goToSignature = () => {
    const el = document.getElementById("sig-field");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    else setPadOpen(true);
  };

  if (!info && err)
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <p className="text-red-400 text-sm text-center">{err}</p>
      </div>
    );
  if (!info)
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-gray-500 text-sm animate-pulse">
        Loading…
      </div>
    );

  const zoomBtn =
    "w-8 h-8 flex items-center justify-center rounded bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-30 text-base leading-none";
  const field = done ? null : info.signatureField;

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 px-3 sm:px-4 py-4 sm:py-6">
      <div className="max-w-4xl mx-auto space-y-4">
        <div>
          <h1 className="text-lg font-semibold text-white">
            Subcontractor Agreement{" "}
            <span className="text-orange-400 font-mono">
              {info.agreementNumber}
            </span>
          </h1>
          <p className="text-xs text-gray-400">
            {info.companyName}
            {info.title ? ` · ${info.title}` : ""}
          </p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
            <span className="text-xs text-gray-400">Agreement</span>
            <div className="flex items-center gap-2">
              {field && (
                <button
                  type="button"
                  onClick={goToSignature}
                  className="px-2.5 h-8 text-xs font-medium text-white rounded hover:brightness-110"
                  style={{ backgroundColor: ACCENT }}
                >
                  Go to signature
                </button>
              )}
              <button
                type="button"
                className={zoomBtn}
                onClick={() =>
                  setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))
                }
                disabled={zoom <= 1}
                aria-label="Zoom out"
              >
                −
              </button>
              <span className="text-xs text-gray-400 w-10 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                className={zoomBtn}
                onClick={() =>
                  setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))
                }
                disabled={zoom >= 3}
                aria-label="Zoom in"
              >
                +
              </button>
            </div>
          </div>

          {pdfBuffer ? (
            <PdfPages
              buffer={pdfBuffer}
              zoom={zoom}
              sigField={field}
              sigImage={sigImage}
              onSign={() => setPadOpen(true)}
            />
          ) : (
            <p className="p-6 text-sm text-gray-500 animate-pulse">
              Loading document…
            </p>
          )}

          {pdfUrl && (
            <div className="px-4 py-2 border-t border-gray-700 text-xs">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="text-orange-400 hover:underline"
              >
                Open PDF in a new tab
              </a>
            </div>
          )}
        </div>

        {done ? (
          <div className="bg-green-900/30 border border-green-700 rounded-xl p-5 text-sm text-green-200">
            ✓ Signed{info.signedBy ? ` by ${info.signedBy}` : ""}. A copy has
            been emailed to you. You can close this page.
          </div>
        ) : (
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 sm:p-5 space-y-4">
            <p className="text-[11px] font-semibold text-orange-400 uppercase tracking-widest">
              Sign this agreement
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              {sigImage ? (
                <span className="text-xs text-green-400">
                  ✓ Signature added
                </span>
              ) : (
                <span className="text-xs text-gray-400">
                  {field
                    ? "Click the dashed box on the signature line in the document."
                    : "Add your signature."}
                </span>
              )}
              <button
                type="button"
                onClick={() =>
                  field && !sigImage ? goToSignature() : setPadOpen(true)
                }
                className="px-3 py-1.5 text-xs font-medium text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600"
              >
                {sigImage
                  ? "Redo signature"
                  : field
                    ? "Go to signature line"
                    : "Draw signature"}
              </button>
            </div>

            <label className="flex items-start gap-2 text-xs text-gray-300">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 w-4 h-4"
              />
              I have read this agreement and agree that my electronic signature
              is legally binding.
            </label>

            {err && <p className="text-xs text-red-400">{err}</p>}

            <button
              onClick={submit}
              disabled={busy}
              className="w-full sm:w-auto px-5 py-3 sm:py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 disabled:opacity-40"
              style={{ backgroundColor: ACCENT }}
            >
              {busy ? "Submitting…" : "Sign Agreement"}
            </button>
          </div>
        )}
      </div>

      {padOpen && (
        <SignaturePadModal
          onCancel={() => setPadOpen(false)}
          onApply={(img) => {
            setSigImage(img);
            setPadOpen(false);
            setErr("");
          }}
        />
      )}
    </div>
  );
}
